<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-2xl font-bold text-[#22282F]">Types de document</h2>
          <p class="text-sm text-gray-500 mt-1">Gérez les modèles de documents et leurs restrictions d'accès</p>
        </div>
        <button
          @click="openCreateModal"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2"
        >
          <Plus class="w-4 h-4" />
          Nouveau type
        </button>
      </div>

      <!-- Barre de recherche + filtres -->
      <div class="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher par nom ou code..."
            class="w-full pl-12 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
          />
        </div>
        <select
          v-model="filterScope"
          class="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
        >
          <option value="all">Tous les types</option>
          <option value="global">Globaux uniquement</option>
          <option value="restricted">Restreints uniquement</option>
        </select>
        <select
          v-model="filterStatus"
          class="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
        >
          <option value="all">Tous statuts</option>
          <option value="active">Actifs</option>
          <option value="inactive">Inactifs</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-16 bg-white border border-gray-200 rounded-xl">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <!-- Tableau -->
      <div v-else-if="filtered.length" class="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-100 bg-gray-50">
              <th class="text-left px-5 py-3 font-semibold text-gray-600">Code</th>
              <th class="text-left px-5 py-3 font-semibold text-gray-600">Nom</th>
              <th class="text-left px-5 py-3 font-semibold text-gray-600">Entité</th>
              <th class="text-left px-5 py-3 font-semibold text-gray-600">Template</th>
              <th class="text-left px-5 py-3 font-semibold text-gray-600">Statut</th>
              <th class="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="dt in filtered"
              :key="dt.id"
              class="border-b border-gray-50 hover:bg-gray-50/70 transition-colors group"
            >
              <td class="px-5 py-4">
                <span class="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{{ dt.code }}</span>
              </td>
              <td class="px-5 py-4">
                <div class="font-medium text-[#22282F]">{{ dt.nom }}</div>
                <span
                  v-if="dt.workflow_template || dt.workflow_rules?.length"
                  class="inline-flex items-center gap-1 mt-1 text-[11px] text-violet-700 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded"
                >
                  <Workflow class="w-3 h-3" /> {{ dt.workflow_template?.nom || 'Aucun workflow par défaut' }}
                  <template v-if="dt.workflow_rules?.length">+ {{ libelleRegles(dt.workflow_rules.length) }}</template>
                </span>
              </td>
              <td class="px-5 py-4">
                <span v-if="dt.direction" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  <Building2 class="w-3 h-3" />
                  {{ dt.direction.code }}
                </span>
                <span v-else-if="dt.ambassade" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                  <Globe class="w-3 h-3" />
                  {{ dt.ambassade.code }}
                </span>
                <span v-else class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  <Globe2 class="w-3 h-3" />
                  Global
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-2">
                  <button
                    v-if="dt.has_template"
                    @click="openPreview(dt)"
                    class="inline-flex items-center gap-1 text-xs text-[#22282F] hover:underline"
                    title="Prévisualiser"
                  >
                    <FileText class="w-3.5 h-3.5" />
                    Voir
                  </button>
                  <span v-else class="text-xs text-gray-400">Aucun</span>
                  <span v-if="dt.has_ai_template" class="text-xs bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded">IA</span>
                </div>
              </td>
              <td class="px-5 py-4">
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    dt.is_active
                      ? 'bg-green-50 text-green-700 border border-green-100'
                      : 'bg-red-50 text-red-700 border border-red-100'
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full', dt.is_active ? 'bg-green-500' : 'bg-red-400']" />
                  {{ dt.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-end gap-1">
                  <button
                    @click="openPreview(dt)"
                    class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Prévisualiser"
                  >
                    <Eye class="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    @click="openEditModal(dt)"
                    class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit3 class="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    @click="askToggleActive(dt)"
                    class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    :title="dt.is_active ? 'Désactiver' : 'Activer'"
                  >
                    <ToggleLeft v-if="dt.is_active" class="w-4 h-4 text-green-600" />
                    <ToggleRight v-else class="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    @click="askDelete(dt)"
                    class="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 class="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div v-else class="bg-white border border-gray-200 rounded-xl p-16 text-center shadow-sm">
        <FileText class="w-14 h-14 text-gray-200 mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-[#22282F] mb-1">Aucun type de document</h3>
        <p class="text-sm text-gray-500">
          {{ searchQuery || filterScope !== 'all' || filterStatus !== 'all' ? 'Aucun résultat pour ces filtres' : 'Créez votre premier type de document' }}
        </p>
      </div>
    </div>

    <!-- ================================================================
         MODAL CRÉATION / ÉDITION
    ================================================================ -->
    <div
      v-if="showFormModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeFormModal"
    >
      <div class="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 class="text-lg font-bold text-[#22282F]">
            {{ editingItem ? 'Modifier le type de document' : 'Nouveau type de document' }}
          </h3>
          <button @click="closeFormModal" class="p-2 hover:bg-gray-100 rounded-lg">
            <X class="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div class="p-6 space-y-5">
          <!-- Code -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Code <span class="text-red-500">*</span></label>
            <input
              v-model="form.code"
              type="text"
              placeholder="ex: NOTE_VERBALE"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 font-mono"
            />
          </div>

          <!-- Nom -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Nom <span class="text-red-500">*</span></label>
            <input
              v-model="form.nom"
              type="text"
              placeholder="ex: Note Verbale"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Description</label>
            <textarea
              v-model="form.description"
              rows="2"
              placeholder="Description optionnelle..."
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 resize-none"
            />
          </div>

          <!-- Restriction d'entité -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-2">Restriction d'accès</label>
            <div class="flex gap-3 mb-3">
              <button
                v-for="opt in scopeOptions"
                :key="opt.value"
                @click="form.scopeType = opt.value"
                :class="[
                  'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                  form.scopeType === opt.value
                    ? 'bg-[#22282F] text-white border-[#22282F]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                ]"
              >
                {{ opt.label }}
              </button>
            </div>
            <select
              v-if="form.scopeType === 'direction'"
              v-model="form.direction_id"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
            >
              <option value="">-- Choisir une direction --</option>
              <option v-for="d in directions" :key="d.id" :value="String(d.id)">{{ d.code }} — {{ d.nom }}</option>
            </select>
            <select
              v-if="form.scopeType === 'ambassade'"
              v-model="form.ambassade_id"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
            >
              <option value="">-- Choisir une ambassade --</option>
              <option v-for="a in ambassades" :key="a.id" :value="String(a.id)">{{ a.code }} — {{ a.nom }}</option>
            </select>
          </div>

          <!-- Template DOCX -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Template DOCX</label>
            <div v-if="editingItem?.has_template && !form.removeTemplate" class="mb-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <FileText class="w-4 h-4 text-gray-400" />
              <span class="flex-1 truncate">Fichier actuel</span>
              <button @click="form.removeTemplate = true" class="text-red-500 hover:text-red-600 text-xs">Supprimer</button>
            </div>
            <input
              v-if="!editingItem?.has_template || form.removeTemplate"
              type="file"
              accept=".docx"
              @change="onTemplateChange"
              class="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
          </div>

          <!-- Template IA -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Template IA (optionnel)</label>
            <div v-if="editingItem?.has_ai_template && !form.removeAiTemplate" class="mb-2 flex items-center gap-2 text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <FileText class="w-4 h-4 text-amber-400" />
              <span class="flex-1 truncate">Template IA actuel</span>
              <button @click="form.removeAiTemplate = true" class="text-red-500 hover:text-red-600 text-xs">Supprimer</button>
            </div>
            <input
              v-if="!editingItem?.has_ai_template || form.removeAiTemplate"
              type="file"
              accept=".docx"
              @change="onAiTemplateChange"
              class="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
            />
          </div>

          <!-- Workflow prédéfini -->
          <div>
            <label class="block text-sm font-medium text-[#22282F] mb-1.5">Workflow de validation</label>
            <select
              v-model="form.workflow_template_id"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
            >
              <option value="">Aucun — circuit saisi manuellement à chaque rédaction</option>
              <option
                v-for="wt in workflowTemplates.filter(w => w.is_active)"
                :key="wt.id"
                :value="String(wt.id)"
              >
                {{ wt.nom }}
              </option>
            </select>
            <p class="text-xs text-gray-400 mt-1">
              Pré-remplit automatiquement le circuit de validation à la rédaction.
              <router-link to="/workflow-templates" class="text-[#22282F] underline">Gérer les workflows</router-link>
            </p>
          </div>

          <!-- Workflow selon le profil du rédacteur -->
          <DocumentTypeWorkflowRulesEditor
            v-model="form.workflow_rules"
            :roles="roles"
            :directions="directions"
            :workflow-templates="workflowTemplates"
          />

          <!-- Options -->
          <div class="flex gap-6">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="form.is_active" class="rounded" />
              <span class="text-sm text-gray-700">Actif</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="form.requires_ai_generation" class="rounded" />
              <span class="text-sm text-gray-700">Flux IA</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="form.requires_reference" class="rounded" />
              <span class="text-sm text-gray-700">Nécessite une référence</span>
            </label>
          </div>
          <p class="text-xs text-gray-400 -mt-4">
            Décocher pour une note interne qui ne sort pas du ministère (ex: note à l'attention de la Ministre) :
            aucun numéro de référence officiel ne sera généré, et le template n'a pas besoin du placeholder [REFERENCE].
          </p>
        </div>

        <div class="flex gap-3 px-6 pb-6">
          <button
            @click="closeFormModal"
            class="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            @click="saveForm"
            :disabled="saving"
            class="flex-1 px-4 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <div v-if="saving" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            {{ editingItem ? 'Enregistrer' : 'Créer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ================================================================
         MODAL PRÉVISUALISATION
    ================================================================ -->
    <div
      v-if="previewItem"
      class="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      @click.self="previewItem = null"
    >
      <div class="bg-white rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col" style="max-height: 90vh;">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div class="flex items-center gap-3">
            <FileText class="w-5 h-5 text-gray-500" />
            <span class="font-semibold text-[#22282F]">Prévisualisation — {{ previewItem.nom }}</span>
          </div>
          <button @click="previewItem = null" class="p-2 hover:bg-gray-100 rounded-lg">
            <X class="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <!-- Corps split : gauche = doc, droite = infos -->
        <div class="flex flex-1 overflow-hidden">

          <!-- Panneau gauche : iframe / fallback -->
          <div class="flex-1 bg-gray-100 border-r border-gray-200 flex items-center justify-center overflow-hidden">
            <div v-if="previewItem.has_template && previewItem.template_url" class="w-full h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
              <FileText class="w-16 h-16 text-gray-300" />
              <p class="text-sm text-gray-500 max-w-xs">La prévisualisation directe des fichiers DOCX n'est pas supportée dans le navigateur.</p>
              <a
                :href="previewItem.template_url"
                target="_blank"
                download
                class="inline-flex items-center gap-2 px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors"
              >
                <Download class="w-4 h-4" />
                Télécharger le template
              </a>
              <a
                v-if="previewItem.has_ai_template && previewItem.ai_template_url"
                :href="previewItem.ai_template_url"
                target="_blank"
                download
                class="inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50 rounded-lg text-sm hover:bg-amber-100 transition-colors"
              >
                <Download class="w-4 h-4" />
                Télécharger le template IA
              </a>
            </div>
            <div v-else class="flex flex-col items-center gap-3 text-gray-400">
              <FileText class="w-14 h-14" />
              <p class="text-sm">Aucun template associé</p>
            </div>
          </div>

          <!-- Panneau droit : informations -->
          <div class="w-72 shrink-0 overflow-y-auto p-6 space-y-5">
            <div>
              <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Identité</p>
              <div class="space-y-2">
                <div>
                  <p class="text-xs text-gray-500">Code</p>
                  <p class="text-sm font-mono font-medium text-[#22282F]">{{ previewItem.code }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Nom</p>
                  <p class="text-sm font-medium text-[#22282F]">{{ previewItem.nom }}</p>
                </div>
                <div v-if="previewItem.description">
                  <p class="text-xs text-gray-500">Description</p>
                  <p class="text-sm text-gray-700">{{ previewItem.description }}</p>
                </div>
              </div>
            </div>

            <div>
              <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Accès</p>
              <div>
                <span v-if="previewItem.direction" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  <Building2 class="w-3 h-3" /> Direction : {{ previewItem.direction.nom }}
                </span>
                <span v-else-if="previewItem.ambassade" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                  <Globe class="w-3 h-3" /> Ambassade : {{ previewItem.ambassade.nom }}
                </span>
                <span v-else class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  <Globe2 class="w-3 h-3" /> Tous les utilisateurs
                </span>
              </div>
            </div>

            <div>
              <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Configuration</p>
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Statut</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600']">
                    {{ previewItem.is_active ? 'Actif' : 'Inactif' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Flux IA</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.requires_ai_generation ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.requires_ai_generation ? 'Oui' : 'Non' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Nécessite une référence</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.requires_reference ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.requires_reference ? 'Oui' : 'Non' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Template standard</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.has_template ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.has_template ? 'Oui' : 'Non' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Template IA</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.has_ai_template ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.has_ai_template ? 'Oui' : 'Non' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Workflow prédéfini</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.workflow_template ? 'bg-violet-50 text-violet-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.workflow_template?.nom || 'Aucun' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Circuits selon le profil</span>
                  <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', previewItem.workflow_rules?.length ? 'bg-violet-50 text-violet-700' : 'bg-gray-100 text-gray-500']">
                    {{ previewItem.workflow_rules?.length ? libelleRegles(previewItem.workflow_rules.length) : 'Aucun' }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Dates</p>
              <div class="space-y-1.5">
                <div>
                  <p class="text-xs text-gray-500">Créé le</p>
                  <p class="text-xs text-gray-700">{{ formatDate(previewItem.created_at) }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Modifié le</p>
                  <p class="text-xs text-gray-700">{{ formatDate(previewItem.updated_at) }}</p>
                </div>
              </div>
            </div>

            <!-- Actions rapides -->
            <div class="pt-2 flex flex-col gap-2">
              <button
                @click="openEditModal(previewItem); previewItem = null"
                class="w-full px-3 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors flex items-center justify-center gap-2"
              >
                <Edit3 class="w-4 h-4" /> Modifier
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================
         MODAL CONFIRMATION (désactivation / suppression)
    ================================================================ -->
    <div
      v-if="confirmModal.show"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="confirmModal.show = false"
    >
      <div class="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
        <div class="flex items-center gap-3 mb-4">
          <div :class="['p-2.5 rounded-full', confirmModal.type === 'delete' ? 'bg-red-100' : 'bg-amber-100']">
            <AlertTriangle :class="['w-5 h-5', confirmModal.type === 'delete' ? 'text-red-600' : 'text-amber-600']" />
          </div>
          <h3 class="text-lg font-bold text-[#22282F]">{{ confirmModal.title }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-6">{{ confirmModal.message }}</p>
        <div class="flex gap-3">
          <button
            @click="confirmModal.show = false"
            class="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            @click="confirmModal.onConfirm(); confirmModal.show = false"
            :class="[
              'flex-1 px-4 py-2.5 rounded-lg text-sm text-white transition-colors',
              confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'
            ]"
          >
            {{ confirmModal.confirmLabel }}
          </button>
        </div>
      </div>
    </div>

  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import {
  Plus, Search, FileText, Edit3, Trash2, Eye, X, Building2, Globe, Globe2,
  ToggleLeft, ToggleRight, AlertTriangle, Download, Workflow
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import DocumentTypeWorkflowRulesEditor from '@/components/DocumentTypeWorkflowRulesEditor.vue'
import type { RegleBrouillon } from '@/types/workflowTemplate'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()

// ─── Types ────────────────────────────────────────────────────────────────────

interface EntityRef { id: number; nom: string; code: string }

interface WorkflowRule {
  id: number
  ordre: number
  roles: EntityRef[]
  direction: EntityRef | null
  workflow_template: { id: number; nom: string; is_active: boolean }
}

interface DocumentType {
  id: number
  code: string
  nom: string
  description: string
  is_active: boolean
  requires_ai_generation: boolean
  requires_reference: boolean
  has_template: boolean
  template_url: string | null
  has_ai_template: boolean
  ai_template_url: string | null
  direction: EntityRef | null
  ambassade: EntityRef | null
  workflow_template: { id: number; nom: string } | null
  workflow_rules: WorkflowRule[]
  created_at: string
  updated_at: string
}

interface WorkflowTemplateOption { id: number; nom: string; is_active: boolean }
interface RoleOption { id: number; code: string; nom: string }

interface Direction { id: number; code: string; nom: string }
interface Ambassade { id: number; code: string; nom: string; pays?: string }

// ─── State ────────────────────────────────────────────────────────────────────

const loading = ref(false)
const saving = ref(false)

const items = ref<DocumentType[]>([])
const directions = ref<Direction[]>([])
const ambassades = ref<Ambassade[]>([])
const workflowTemplates = ref<WorkflowTemplateOption[]>([])
const roles = ref<RoleOption[]>([])

const searchQuery = ref('')
const filterScope = ref<'all' | 'global' | 'restricted'>('all')
const filterStatus = ref<'all' | 'active' | 'inactive'>('all')

const showFormModal = ref(false)
const editingItem = ref<DocumentType | null>(null)
const previewItem = ref<DocumentType | null>(null)

const scopeOptions = [
  { value: 'global', label: 'Global' },
  { value: 'direction', label: 'Direction' },
  { value: 'ambassade', label: 'Ambassade' },
]

const emptyForm = () => ({
  code: '',
  nom: '',
  description: '',
  is_active: true,
  requires_ai_generation: false,
  requires_reference: true,
  scopeType: 'global' as 'global' | 'direction' | 'ambassade',
  direction_id: '',
  ambassade_id: '',
  templateFile: null as File | null,
  aiTemplateFile: null as File | null,
  removeTemplate: false,
  removeAiTemplate: false,
  workflow_template_id: '',
  workflow_rules: [] as RegleBrouillon[],
})

const form = ref(emptyForm())

const confirmModal = reactive({
  show: false,
  type: 'delete' as 'delete' | 'toggle',
  title: '',
  message: '',
  confirmLabel: '',
  onConfirm: () => {},
})

// ─── Computed ─────────────────────────────────────────────────────────────────

const filtered = computed(() => {
  let list = items.value

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(dt => dt.nom.toLowerCase().includes(q) || dt.code.toLowerCase().includes(q))
  }

  if (filterScope.value === 'global') {
    list = list.filter(dt => !dt.direction && !dt.ambassade)
  } else if (filterScope.value === 'restricted') {
    list = list.filter(dt => dt.direction || dt.ambassade)
  }

  if (filterStatus.value === 'active') {
    list = list.filter(dt => dt.is_active)
  } else if (filterStatus.value === 'inactive') {
    list = list.filter(dt => !dt.is_active)
  }

  return list
})

// ─── Fetch ────────────────────────────────────────────────────────────────────

const fetchAll = async () => {
  loading.value = true
  try {
    const [dts, dirs, ambs, wts, rls] = await Promise.all([
      apiFetch<DocumentType[]>('/api/admin/document-types/'),
      apiFetch<Direction[]>('/api/admin/directions/'),
      apiFetch<Ambassade[]>('/api/admin/ambassades/'),
      apiFetch<WorkflowTemplateOption[]>('/api/admin/workflow-templates/'),
      apiFetch<RoleOption[]>('/api/admin/roles/'),
    ])
    items.value = dts
    directions.value = dirs
    ambassades.value = ambs
    workflowTemplates.value = wts
    roles.value = rls
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les données')
  } finally {
    loading.value = false
  }
}

// ─── Modals ───────────────────────────────────────────────────────────────────

const openCreateModal = () => {
  editingItem.value = null
  form.value = emptyForm()
  showFormModal.value = true
}

const openEditModal = (dt: DocumentType) => {
  editingItem.value = dt
  form.value = {
    code: dt.code,
    nom: dt.nom,
    description: dt.description,
    is_active: dt.is_active,
    requires_ai_generation: dt.requires_ai_generation,
    requires_reference: dt.requires_reference,
    scopeType: dt.direction ? 'direction' : dt.ambassade ? 'ambassade' : 'global',
    direction_id: dt.direction ? String(dt.direction.id) : '',
    ambassade_id: dt.ambassade ? String(dt.ambassade.id) : '',
    templateFile: null,
    aiTemplateFile: null,
    removeTemplate: false,
    removeAiTemplate: false,
    workflow_template_id: dt.workflow_template ? String(dt.workflow_template.id) : '',
    workflow_rules: (dt.workflow_rules ?? []).map(r => ({
      role_ids: r.roles.map(role => role.id),
      direction_id: r.direction ? String(r.direction.id) : '',
      workflow_template_id: String(r.workflow_template.id),
    })),
  }
  showFormModal.value = true
}

const closeFormModal = () => {
  showFormModal.value = false
  editingItem.value = null
}

const openPreview = (dt: DocumentType) => {
  previewItem.value = dt
}

// ─── File handlers ────────────────────────────────────────────────────────────

const onTemplateChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0] ?? null
  form.value.templateFile = file
}

const onAiTemplateChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0] ?? null
  form.value.aiTemplateFile = file
}

// ─── Save ─────────────────────────────────────────────────────────────────────

const saveForm = async () => {
  if (!form.value.code.trim() || !form.value.nom.trim()) {
    toast.error('Validation', 'Le code et le nom sont obligatoires')
    return
  }
  if (form.value.scopeType === 'direction' && !form.value.direction_id) {
    toast.error('Validation', 'Veuillez sélectionner une direction')
    return
  }
  if (form.value.scopeType === 'ambassade' && !form.value.ambassade_id) {
    toast.error('Validation', 'Veuillez sélectionner une ambassade')
    return
  }
  const regleIncomplete = form.value.workflow_rules.findIndex(r => !r.role_ids.length || !r.workflow_template_id)
  if (regleIncomplete !== -1) {
    toast.error('Validation', `Règle ${regleIncomplete + 1} : choisissez au moins un rôle et un workflow`)
    return
  }

  saving.value = true
  try {
    const fd = new FormData()
    fd.append('code', form.value.code.trim().toUpperCase())
    fd.append('nom', form.value.nom.trim())
    fd.append('description', form.value.description.trim())
    fd.append('is_active', String(form.value.is_active))
    fd.append('requires_ai_generation', String(form.value.requires_ai_generation))
    fd.append('requires_reference', String(form.value.requires_reference))

    if (form.value.scopeType === 'direction') {
      fd.append('direction_id', form.value.direction_id)
    } else if (form.value.scopeType === 'ambassade') {
      fd.append('ambassade_id', form.value.ambassade_id)
    } else {
      fd.append('direction_id', '')
      fd.append('ambassade_id', '')
    }

    fd.append('workflow_template_id', form.value.workflow_template_id)
    fd.append('workflow_rules', JSON.stringify(form.value.workflow_rules.map(r => ({
      role_ids: r.role_ids,
      direction_id: r.direction_id ? Number(r.direction_id) : null,
      workflow_template_id: Number(r.workflow_template_id),
    }))))

    if (form.value.templateFile) fd.append('template_docx', form.value.templateFile)
    if (form.value.aiTemplateFile) fd.append('ai_template_docx', form.value.aiTemplateFile)
    if (form.value.removeTemplate) fd.append('remove_template', 'true')
    if (form.value.removeAiTemplate) fd.append('remove_ai_template', 'true')

    const url = editingItem.value
      ? `/api/admin/document-types/${editingItem.value.id}/`
      : '/api/admin/document-types/'

    const saved = await apiFetch<DocumentType>(url, {
      method: editingItem.value ? 'PUT' : 'POST',
      body: fd,
      headers: {},
    })

    if (editingItem.value) {
      const idx = items.value.findIndex(d => d.id === saved.id)
      if (idx !== -1) items.value[idx] = saved
    } else {
      items.value.push(saved)
    }

    toast.success(editingItem.value ? 'Type modifié' : 'Type créé', 'L\'opération a réussi')
    closeFormModal()
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de sauvegarder')
  } finally {
    saving.value = false
  }
}

// ─── Toggle active ────────────────────────────────────────────────────────────

const askToggleActive = (dt: DocumentType) => {
  confirmModal.type = 'toggle'
  confirmModal.title = dt.is_active ? 'Désactiver ce type ?' : 'Activer ce type ?'
  confirmModal.message = dt.is_active
    ? `Le type « ${dt.nom} » sera masqué des utilisateurs. Vous pourrez le réactiver à tout moment.`
    : `Le type « ${dt.nom} » redeviendra visible pour les utilisateurs concernés.`
  confirmModal.confirmLabel = dt.is_active ? 'Désactiver' : 'Activer'
  confirmModal.onConfirm = () => doToggleActive(dt)
  confirmModal.show = true
}

const doToggleActive = async (dt: DocumentType) => {
  try {
    const result = await apiFetch<{ id: number; is_active: boolean }>(
      `/api/admin/document-types/${dt.id}/toggle-active/`,
      { method: 'PATCH' }
    )
    const idx = items.value.findIndex(d => d.id === dt.id)
    if (idx !== -1) items.value[idx] = { ...items.value[idx], is_active: result.is_active }
    toast.success(
      result.is_active ? 'Type activé' : 'Type désactivé',
      `« ${dt.nom} » a été mis à jour`
    )
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de modifier le statut')
  }
}

// ─── Delete ───────────────────────────────────────────────────────────────────

const askDelete = (dt: DocumentType) => {
  confirmModal.type = 'delete'
  confirmModal.title = 'Supprimer ce type ?'
  confirmModal.message = `La suppression du type « ${dt.nom} » (${dt.code}) est irréversible. Les documents existants utilisant ce type ne seront pas affectés, mais il ne sera plus proposé à la création.`
  confirmModal.confirmLabel = 'Supprimer'
  confirmModal.onConfirm = () => doDelete(dt)
  confirmModal.show = true
}

const doDelete = async (dt: DocumentType) => {
  try {
    await apiFetch(`/api/admin/document-types/${dt.id}/`, { method: 'DELETE' })
    items.value = items.value.filter(d => d.id !== dt.id)
    toast.success('Type supprimé', `« ${dt.nom} » a été supprimé`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de supprimer ce type')
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const libelleRegles = (n: number) => `${n} règle${n > 1 ? 's' : ''}`

const formatDate = (iso: string) => {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

onMounted(fetchAll)
</script>
